import {
  vArray,
  vBoolStrict,
  vCheck,
  vObject,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
} from '../validation/index.ts'

const isValidWebhookUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

export const WEBHOOK_EVENTS = [
  { value: 'shipment.created', label: 'Created' },
  {
    value: 'shipment.spot_quote_added',
    label: 'Spot quote added',
  },
  {
    value: 'shipment.spot_quote_declined',
    label: 'Spot quote declined',
  },
  { value: 'shipment.booked', label: 'Booked' },
  { value: 'shipment.confirmed', label: 'Confirmed' },
  {
    value: 'shipment.booking_failed',
    label: 'Booking failed',
  },
  { value: 'shipment.canceled', label: 'Canceled' },
  { value: 'shipment.uncanceled', label: 'Uncanceled' },
  { value: 'shipment.picked_up', label: 'Picked up' },
  {
    value: 'shipment.out_for_delivery',
    label: 'Out for delivery',
  },
  { value: 'shipment.delivered', label: 'Delivered' },
  { value: 'shipment.edited', label: 'Edited' },
  {
    value: 'shipment.documents_added',
    label: 'Documents added',
  },
  { value: 'shipment.rejected', label: 'Rejected' },
  { value: 'shipment.retracted', label: 'Retracted' },
  { value: 'shipment.archived', label: 'Archived' },
  { value: 'shipment.invoice_accepted', label: 'Invoice accepted' },
  { value: 'shipment.invoice_disputed', label: 'Invoice disputed' },
  { value: 'shipment.invoice_archived', label: 'Invoice archived' },
] as const

export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number]['value']

export const WEBHOOK_EVENT_VALUES = WEBHOOK_EVENTS.map(e => e.value) as [
  WebhookEvent,
  ...WebhookEvent[],
]

export const WEBHOOK_ENVIRONMENTS = ['production', 'sandbox'] as const

export type WebhookEnvironment = (typeof WEBHOOK_ENVIRONMENTS)[number]

const webhookUrlSchema = vPipe(
  vString(),
  vCheck(isValidWebhookUrl, 'Must be a valid URL'),
)

export const webhookUpsertSchema = vPipe(
  vObject({
    url: vOptional(webhookUrlSchema),
    sandboxUrl: vOptional(webhookUrlSchema),
    enabled: vOptional(vBoolStrict()),
    sandboxEnabled: vOptional(vBoolStrict()),
    events: vOptional(vArray(vPicklist(WEBHOOK_EVENT_VALUES))),
  }),
  vCheck(
    dto => !!dto.url || !!dto.sandboxUrl,
    'A webhook URL or sandbox URL is required',
  ),
)

export type WebhookUpsertDto = vOutput<typeof webhookUpsertSchema>

export const webhookEnabledSchema = vObject({
  enabled: vBoolStrict(),
  environment: vOptional(vPicklist(WEBHOOK_ENVIRONMENTS)),
})

export type WebhookEnabledDto = vOutput<typeof webhookEnabledSchema>

export const webhookTestSchema = vObject({
  environment: vOptional(vPicklist(WEBHOOK_ENVIRONMENTS)),
})

export type WebhookTestDto = vOutput<typeof webhookTestSchema>

export interface WebhookConfigDto {
  url?: string
  sandboxUrl?: string
  enabled: boolean
  sandboxEnabled?: boolean
  createdDate: Date
  events?: WebhookEvent[]
  failingSince?: Date
  sandboxFailingSince?: Date
}

export interface WebhookSecretDto {
  signingSecret: string
}

// The plaintext secret is only returned when it is first generated or rotated.
export interface WebhookUpsertResultDto extends WebhookConfigDto {
  signingSecret?: string
}
